import { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import chartData from '../../data/chartData.json'
import {
  chartColors,
  lineDefaults,
  mergeOptions,
  withOpacity
} from '../../theme/chartTheme'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const ageLabels = {
  '0_to_5_years': '0-5 years',
  '5_to_10_years': '5-10 years',
  '10_to_15_years': '10-15 years',
  '15_to_20_years': '15-20 years',
  'over_20_years': '20+ years'
}

const orderedKeys = [
  '0_to_5_years',
  '5_to_10_years',
  '10_to_15_years',
  '15_to_20_years',
  'over_20_years'
]

function getAgeBucket(age) {
  if (age == null) return null
  if (age <= 5) return '0_to_5_years'
  if (age <= 10) return '5_to_10_years'
  if (age <= 15) return '10_to_15_years'
  if (age <= 20) return '15_to_20_years'
  return 'over_20_years'
}

function AgePerformance({ data: filterData, onPointClick }) {
  // Compute age stats from filtered data or use static data
  const ageData = useMemo(() => {
    if (filterData && filterData.length > 0) {
      const byAge = {}

      filterData.forEach(study => {
        if (study.equipment_age == null || !study.efficiency_pct) return

        const bucket = getAgeBucket(study.equipment_age)
        if (!bucket) return

        if (!byAge[bucket]) {
          byAge[bucket] = {
            efficiencies: []
          }
        }

        byAge[bucket].efficiencies.push(study.efficiency_pct)
      })

      // Convert to same format as chartData
      const result = {}
      orderedKeys.forEach(key => {
        if (byAge[key] && byAge[key].efficiencies.length > 0) {
          result[key] = {
            average_improvement_pct: byAge[key].efficiencies.reduce((a, b) => a + b, 0) / byAge[key].efficiencies.length,
            case_count: byAge[key].efficiencies.length
          }
        } else {
          result[key] = {
            average_improvement_pct: 0,
            case_count: 0
          }
        }
      })

      return result
    }

    return chartData.by_age
  }, [filterData])

  const labels = orderedKeys.map(key => ageLabels[key])
  const avgImprovement = orderedKeys.map(key => ageData[key]?.average_improvement_pct || 0)
  const caseCounts = orderedKeys.map(key => ageData[key]?.case_count || 0)

  const data = {
    labels,
    datasets: [
      {
        label: 'Avg Improvement %',
        data: avgImprovement,
        borderColor: chartColors.primary.main,
        backgroundColor: withOpacity(chartColors.primary.main, 0.15),
        fill: true,
        tension: lineDefaults.tension,
        borderWidth: 3,
        pointRadius: 6,
        pointBackgroundColor: chartColors.primary.main,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: chartColors.primary.light,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
        yAxisID: 'y'
      },
      {
        label: 'Case Count',
        data: caseCounts,
        borderColor: chartColors.info,
        backgroundColor: 'transparent',
        borderDash: [6, 4],
        tension: lineDefaults.tension,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: chartColors.info,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        yAxisID: 'y1'
      }
    ]
  }

  // Dynamically adjust y-axis based on data
  const maxImprovement = Math.max(...avgImprovement.filter(v => v > 0), 30)
  const minImprovement = Math.min(...avgImprovement.filter(v => v > 0), 15)

  const options = mergeOptions({
    interaction: {
      mode: 'index',
      intersect: false
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onPointClick) {
        const index = elements[0].index
        const ageKey = orderedKeys[index]
        onPointClick('ageRange', ageKey)
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11,
            weight: 500
          },
          color: chartColors.textSecondary
        }
      },
      tooltip: {
        callbacks: {
          title: (context) => `Equipment Age: ${context[0].label}`,
          afterBody: (context) => {
            const key = orderedKeys[context[0].dataIndex]
            const age = ageData[key]
            if (age?.recommendation) {
              return [`\nNote: ${age.recommendation.slice(0, 60)}...`]
            }
            return []
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Improvement %',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        min: Math.max(0, Math.floor((minImprovement - 5) / 5) * 5),
        max: Math.ceil((maxImprovement + 5) / 5) * 5,
        grid: {
          color: chartColors.grid
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: {
          display: true,
          text: 'Case Count',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          drawOnChartArea: false
        },
        min: 0,
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      x: {
        title: {
          display: true,
          text: 'Equipment Age',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      }
    }
  })

  return <Line data={data} options={options} />
}

export default AgePerformance
